import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import api from '../../services/api';

// Load Stripe only if key is available
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const PaymentForm = ({ paymentData, selectedAmount, selectedItems, onPaymentSuccess, onPaymentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    // Get client secret for payment intent with selected items
    const createPaymentIntent = async () => {
      try {
        const response = await api.post(`/v1/payments/${paymentData.token}/create-intent/`, {
          amount: selectedAmount,
          selected_items: selectedItems
        });
        setClientSecret(response.data.client_secret);
      } catch (error) {
        onPaymentError('Failed to initialize payment');
      }
    };

    if (paymentData.token && selectedAmount > 0) {
      createPaymentIntent();
    }
  }, [paymentData.token, selectedAmount, selectedItems, onPaymentError]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setIsProcessing(true);

    const card = elements.getElement(CardElement);

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: card,
        billing_details: {
          name: paymentData.customer.name,
          email: paymentData.customer.email,
        },
      },
    });

    if (error) {
      onPaymentError(error.message);
      setIsProcessing(false);
    } else if (paymentIntent.status === 'succeeded') {
      // Confirm payment on backend with selected items
      try {
        await api.post(`/v1/payments/${paymentData.token}/confirm/`, {
          amount: selectedAmount,
          selected_items: selectedItems
        });
        onPaymentSuccess();
      } catch (confirmError) {
        onPaymentError('Payment processed but confirmation failed. Please contact support.');
      }
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Details
          </Typography>
          <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <CardElement options={cardElementOptions} />
          </Box>
        </CardContent>
      </Card>

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={!stripe || isProcessing || selectedAmount === 0}
        sx={{ py: 2 }}
      >
        {isProcessing ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          `Pay £${selectedAmount.toFixed(2)}`
        )}
      </Button>
    </form>
  );
};

const PaymentCheckout = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentState, setPaymentState] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});

  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/v1/payments/${token}/`);
        if (response.data?.status && response.data.status !== 'pending') {
          setPaymentState(response.data);
          setPaymentData(null);
          setSelectedItems({});
          setError(null);
          return;
        }
        setPaymentData({ ...response.data, token });

        // Initialize all items as selected by default
        const initialSelection = {};
        response.data.items.forEach((item, index) => {
          initialSelection[index] = true;
        });
        setSelectedItems(initialSelection);

        setError(null);
      } catch (err) {
        setError(err.message || 'Payment not found or expired');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPaymentData();
    }
  }, [token]);

  const handlePaymentSuccess = () => {
    setPaymentSuccess(true);
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleItemToggle = (index) => {
    setSelectedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleSelectAll = (selectAll) => {
    const newSelection = {};
    paymentData.items.forEach((item, index) => {
      newSelection[index] = selectAll;
    });
    setSelectedItems(newSelection);
  };

  const calculateSelectedTotal = () => {
    if (!paymentData) return 0;

    const selectedItemsTotal = paymentData.items.reduce((total, item, index) => {
      if (selectedItems[index]) {
        return total + item.total;
      }
      return total;
    }, 0);

    // Always add labor cost (non-selectable)
    const laborCost = paymentData.labor_cost || 0;
    return selectedItemsTotal + laborCost;
  };

  const getSelectedItems = () => {
    if (!paymentData) return [];

    return paymentData.items.filter((item, index) => selectedItems[index]);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>
          Go to Homepage
        </Button>
      </Container>
    );
  }

  if (paymentState) {
    const status = paymentState.status || 'completed';
    const statusCopy = {
      completed: {
        title: 'Payment already completed',
        body: 'This payment link has already been used. If you need a receipt or have questions, please contact the garage.'
      },
      processing: {
        title: 'Payment is processing',
        body: 'Your payment is currently processing. You’ll receive a confirmation once it is complete.'
      },
      failed: {
        title: 'Payment failed',
        body: 'This payment attempt did not complete. Please contact the garage if you need assistance.'
      },
      cancelled: {
        title: 'Payment cancelled',
        body: 'This payment link has been cancelled. Please contact the garage for a new link.'
      },
      refunded: {
        title: 'Payment refunded',
        body: 'This payment was refunded. If you have questions, please contact the garage.'
      },
      expired: {
        title: 'Payment link expired',
        body: 'This payment link has expired. Please contact the garage for a new link.'
      }
    }[status] || {
      title: 'Payment not available',
      body: 'This payment link is no longer active. Please contact the garage for assistance.'
    };

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            {statusCopy.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {statusCopy.body}
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/')}>
            Go to Homepage
          </Button>
        </Paper>
      </Container>
    );
  }

  if (paymentSuccess) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" color="success.main" gutterBottom>
            ✅ Payment Successful!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Thank you for your payment. Your vehicle service will resume shortly.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You should receive a confirmation email shortly.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Typography variant="h4" gutterBottom>
          Payment Required
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Additional parts payment for your vehicle service
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Service Details */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Service Details
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Customer:</strong> {paymentData.customer.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Email:</strong> {paymentData.customer.email}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Vehicle:</strong> {paymentData.booking.vehicle.make} {paymentData.booking.vehicle.model} ({paymentData.booking.vehicle.registration})
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Service Date:</strong> {paymentData.booking.booking_date} {paymentData.booking.time_of_day}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Summary
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography>Original Service:</Typography>
                    <Typography>£{paymentData.booking.original_cost.toFixed(2)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography>Total Additional Parts:</Typography>
                    <Typography
                      sx={{
                        textDecoration: calculateSelectedTotal() < paymentData.amount ? 'line-through' : 'none',
                        color: calculateSelectedTotal() < paymentData.amount ? 'text.secondary' : 'text.primary'
                      }}
                    >
                      £{paymentData.amount.toFixed(2)}
                    </Typography>
                  </Box>
                  {calculateSelectedTotal() < paymentData.amount && (
                    <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography color="primary">Selected Parts:</Typography>
                      <Typography color="primary">
                        £{calculateSelectedTotal().toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">Amount Due:</Typography>
                    <Typography variant="h6" color="primary">
                      £{calculateSelectedTotal().toFixed(2)}
                    </Typography>
                  </Box>
                  {calculateSelectedTotal() === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Please select at least one item to proceed with payment.
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Items List */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">
            Additional Parts Required
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={Object.values(selectedItems).every(Boolean)}
                indeterminate={
                  Object.values(selectedItems).some(Boolean) &&
                  !Object.values(selectedItems).every(Boolean)
                }
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            }
            label="Select All"
          />
        </Box>
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Typography variant="caption">Select</Typography>
                </TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Part Number</TableCell>
                <TableCell align="center">Quantity</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentData.items.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{
                    opacity: selectedItems[index] ? 1 : 0.5,
                    transition: 'opacity 0.2s'
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedItems[index] || false}
                      onChange={() => handleItemToggle(index)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${getPriorityIcon(item.priority)} ${item.priority.toUpperCase()}`}
                      color={getPriorityColor(item.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {item.part_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{item.quantity}</TableCell>
                  <TableCell align="right">£{item.price.toFixed(2)}</TableCell>
                  <TableCell align="right">£{item.total.toFixed(2)}</TableCell>
                </TableRow>
              ))}

              {/* Labor Cost Row (Non-selectable) */}
              {paymentData.labor_cost > 0 && (
                <TableRow sx={{ backgroundColor: '#f5f5f5', borderTop: '2px solid #e0e0e0' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={true}
                      disabled={true}
                      sx={{ color: '#666' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label="⚙️ LABOR"
                      color="info"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Labor Cost
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (Required - Cannot be deselected)
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  </TableCell>
                  <TableCell align="center">1</TableCell>
                  <TableCell align="right">£{paymentData.labor_cost.toFixed(2)}</TableCell>
                  <TableCell align="right">£{paymentData.labor_cost.toFixed(2)}</TableCell>
                </TableRow>
              )}

              {/* Total Row for Selected Items */}
              <TableRow>
                <TableCell colSpan={6} align="right">
                  <Typography variant="h6">
                    Selected Items Total:
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="h6" color="primary">
                    £{calculateSelectedTotal().toFixed(2)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Payment Form */}
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <PaymentForm
              paymentData={paymentData}
              selectedAmount={calculateSelectedTotal()}
              selectedItems={getSelectedItems()}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
            />
          </Elements>
        ) : (
          <Alert severity="error" sx={{ mb: 2 }}>
            Payment system is not configured. Please contact support.
          </Alert>
        )}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Secure Payment:</strong> Your payment is processed securely through Stripe.
            After payment, work will resume immediately on your vehicle.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentCheckout;
